import { ErrorModal } from '@app/components/ErrorModal';
import {
  useAddCartLineItem,
  useCartAndDeliveryDatesDataContext,
} from '@app/data/clients/cartCheckoutClient';
import {
  AddLineItemError,
  getHighestPriorityAddItemErrorType,
} from '@app/pages/i/components/ProductDetailsSection/utils';
import { getImageUrl, publicShopBffSdk } from '@app/utils/common';
import {
  Box,
  Button,
  constants,
  Field,
  FieldMessage,
  FieldRow,
  FieldSet,
  Form,
  Text,
} from '@redbubble/design-system';
import { useViewportWidth } from '@redbubble/next/hooks/use-viewport-width';
import { defineMessages, useIntl } from '@redbubble/next/intl';
import { AppProps, DeviceType, UserInfo } from '@redbubble/next/types';
import { usePageProps, useUserInfo } from '@redbubble/next-provider';
import { useState } from 'react';
import { DEFAULT_VALUE, VALUE_OPTIONS } from '../../constants';

export const GIFT_CARD_FORM_ID = 'gift-card-form';

import { asCurrency } from '../../helpers';
import { GiftCardFormAmount, GiftCardValueOption } from '../../types';
import styles from './GiftCardForm.module.scss';

const messages = defineMessages({
  amount: {
    defaultMessage: 'Amount',
  },
  other: {
    defaultMessage: 'Other',
  },
  selectAmount: {
    defaultMessage: 'Specify an amount',
  },
  enterNumber: {
    defaultMessage: 'Enter a whole number, between 5 and 2000',
  },
  completeMessage: {
    defaultMessage:
      'We’ll send you an email with a link to your digital gift card. Simple.',
  },
  addToCart: {
    defaultMessage: 'Add To Cart',
  },
});

interface GiftCardFormProps {
  onChange: (data: { values: GiftCardFormAmount }) => void;
  onAddToCart: () => void;
}

export const GiftCardForm: React.FC<GiftCardFormProps> = ({
  onChange,
  onAddToCart,
}) => {
  const [{ currency, locale, userId }, setUserInfo] = useUserInfo<UserInfo>();
  const { deviceType } = usePageProps<AppProps>();
  const { isOrAbove } = useViewportWidth(deviceType);
  const isDesktop = isOrAbove(DeviceType.Desktop);
  const intl = useIntl();

  const options = [
    ...VALUE_OPTIONS.map((val) => {
      const option: GiftCardValueOption = {
        label: asCurrency(val, intl, currency),
        value: val,
      };
      return option;
    }),
    { label: intl.formatMessage(messages.other), value: 'other' },
  ];

  const [isSubmitting, setSubmitting] = useState(false);
  const { dispatch, client } = useCartAndDeliveryDatesDataContext();
  const { addGiftCard } = useAddCartLineItem({
    client,
    dispatch,
  });

  const [errorModalState, setErrorModalState] = useState<{
    open: boolean;
    type: 'retryable';
    onRetry: () => void;
  }>({
    open: false,
    type: 'retryable',
    onRetry: () => {},
  });

  const handleAddToCartClick = async (values) => {
    setSubmitting(true);
    if (!userId) {
      const { shop_createAnonymousUser: shopAnonymousUser } =
        await publicShopBffSdk.CreateAnonymousUser({
          locale,
        });

      setUserInfo((currentUserInfo) => {
        if (shopAnonymousUser?.userId) {
          return {
            ...currentUserInfo,
            userId: Number(shopAnonymousUser.userId),
          };
        }

        return currentUserInfo;
      });
    }

    const { errors } = await addGiftCard({
      giftCardAmount:
        values.amount === 'other' ? values.otherAmount : values.amount,
    });

    if (errors?.length) {
      const errorType = getHighestPriorityAddItemErrorType(errors);

      if (errorType === AddLineItemError.Retryable) {
        setErrorModalState({
          open: true,
          type: 'retryable',
          onRetry: () => handleAddToCartClick(values),
        });
      }
      setSubmitting(false);
      return;
    }

    if (!errors?.length) {
      setSubmitting(false);
      onAddToCart();
      setErrorModalState({
        open: false,
        type: 'retryable',
        onRetry: () => {},
      });
    }
  };

  return (
    <Form
      onChange={onChange}
      initialValues={{ amount: DEFAULT_VALUE, otherAmount: DEFAULT_VALUE }}
      onSubmit={(values, { setSubmitting }) => {
        return handleAddToCartClick(values).then(() => setSubmitting(false));
      }}
    >
      {({ errors, values }) => {
        const isOther = values.amount === 'other';
        return (
          <FieldSet>
            <FieldRow>
              <Field
                type={isDesktop ? 'button-toggle' : 'select'}
                fluid={!isDesktop}
                label={intl.formatMessage(messages.amount)}
                name="amount"
                options={options}
              >
                {() => {
                  errors?.name && (
                    <FieldMessage intent={constants.ERROR}>
                      {errors.name}
                    </FieldMessage>
                  );
                }}
              </Field>
            </FieldRow>
            {isOther && (
              <FieldRow>
                <Field
                  type="number"
                  fluid={!isDesktop}
                  label={intl.formatMessage(messages.selectAmount)}
                  name="otherAmount"
                  placeholder="e.g 25"
                >
                  {(field) =>
                    field.focused && (
                      <FieldMessage intent={constants.INFORMATION}>
                        {intl.formatMessage(messages.enterNumber)}
                      </FieldMessage>
                    )
                  }
                </Field>
              </FieldRow>
            )}
            <Button
              type="submit"
              strong
              fluid={!isDesktop}
              intent={constants.PRIMARY}
              size={constants.LARGE}
              loading={isSubmitting}
            >
              <Text>
                <Text>{intl.formatMessage(messages.addToCart)}</Text>
              </Text>
            </Button>
            <ErrorModal
              type={errorModalState.type}
              open={errorModalState.open}
              imageSrc={getImageUrl('/icons/empty-cart.svg')}
              onRetry={errorModalState.onRetry}
              onClose={() =>
                setErrorModalState((prev) => ({ ...prev, open: false }))
              }
            />
            <Box className={styles.completeMessage}>
              <Text type="body2">
                {intl.formatMessage(messages.completeMessage)}
              </Text>
            </Box>
          </FieldSet>
        );
      }}
    </Form>
  );
};
